import useAuth from "@/components/auth/useAuth"
import { TextField } from "@/components/util/form/fields"
import Submit from "@/components/util/form/Submit"
import { Button, makeStyles, Typography } from "@material-ui/core"
import { AxiosError } from "axios"
import { Field, Form, Formik, FormikHelpers } from "formik"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import * as Yup from "yup"

const useStyles = makeStyles(theme => ({
  heading: {
    fontSize: "20px",
    marginBottom: theme.spacing(2)
  },
  subheading: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  submit: {
    marginTop: theme.spacing(4),
    padding: 8,
    fontSize: "16px"
  },
  resendButton: {
    marginTop: theme.spacing(2),
    textTransform: "none"
  },
  cancelButton: {
    marginTop: theme.spacing(1)
  }
}))

interface MfaFormProps {
  method: string
  onCancel: () => void
}

export default function MfaForm({ method, onCancel }: MfaFormProps) {
  const classes = useStyles()
  let location = useLocation()
  let history = useHistory()
  const { verifyMfa, resendMfa } = useAuth()
  const { t } = useTranslation()
  const [resendStatus, setResendStatus] = useState("")
  const methodLabel = method === "sms" ? "phone" : "email"

  const MfaSchema = Yup.object().shape({
    token: Yup.string()
      .required(t("Required"))
      .length(6, t("Must be 6 characters"))
  })

  const handleResend = async () => {
    try {
      await resendMfa()
      setResendStatus(t(`Code resent to your ${methodLabel}.`))
    } catch (e) {
      const error = e as AxiosError<{ message: string }>
      const errorMessage = error.response?.data?.message
      setResendStatus(errorMessage ?? t("Failed to resend code."))
    }
  }

  return (
    <Formik
      initialValues={{ token: "" }}
      validationSchema={MfaSchema}
      onSubmit={(
        { token },
        { setStatus, setSubmitting }: FormikHelpers<any>
      ) => {
        verifyMfa(token)
          .then(() => {
            const { from } = (location.state as any) || {
              from: { pathname: "/" }
            }
            history.push(from)
          })
          .catch(error => {
            // Handle 422 errors similar to LoginForm
            if (error.response && error.response.data.errors) {
              setStatus(error.response.data.errors)
            } else {
              setStatus({ token: t("Invalid token") })
            }
            setSubmitting(false)
          })
      }}
    >
      <Form>
        <Typography className={classes.heading}>
          {t("Input One-Time Token")}
        </Typography>
        <Typography variant="body2" className={classes.subheading}>
          {t(`Please enter the 6-char code sent to your ${methodLabel}`)}
        </Typography>

        <Field
          name="token"
          label={t("One-Time Token")}
          placeholder="VISUAL"
          component={TextField}
          fullWidth
          inputProps={{
            style: {
              letterSpacing: 4,
              textAlign: "center",
              textTransform: "uppercase"
            }
          }}
        />

        <Submit label={t("Verify")} fullWidth className={classes.submit} />

        <Button
          fullWidth
          className={classes.resendButton}
          onClick={handleResend}
          color="primary"
        >
          {t("Resend Token")}
        </Button>
        {resendStatus && (
          <Typography
            variant="caption"
            color="secondary"
            align="center"
            display="block"
          >
            {resendStatus}
          </Typography>
        )}

        <Button fullWidth className={classes.cancelButton} onClick={onCancel}>
          {t("Cancel Login")}
        </Button>
      </Form>
    </Formik>
  )
}
